/**
 * Error handling utilities for API responses
 * Provides user-friendly error messages and logging
 */

export interface ErrorResponse {
  detail: string;
  error_code?: string;
  error_id?: string;
  timestamp?: string;
  details?: any;
  field_errors?: Record<string, string>;
}

export interface ApiError {
  response?: {
    data?: ErrorResponse | any;
    status?: number;
  };
  message?: string;
}

/**
 * Extract user-friendly error message from API error response
 */
export function getErrorMessage(error: ApiError): string {
  const responseData = error.response?.data;
  
  // Check for `detail` field (FastAPI standard — sole message source)
  if (responseData && 'detail' in responseData && responseData.detail) {
    return Array.isArray(responseData.detail)
      ? responseData.detail.map((err: any) => err.msg || err).join(', ')
      : responseData.detail;
  }

  // Default messages based on status code
  const status = error.response?.status;
  if (status) {
    switch (status) {
      case 400:
        return 'Please check your input and try again.';
      case 401:
        return 'Your session has expired. Please log in again.';
      case 403:
        return 'You don\'t have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This item already exists. Please use different information.';
      case 422:
        return 'Some of the information you entered is invalid.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'We\'re experiencing technical difficulties. Please try again later.';
      case 503:
        return 'The service is temporarily unavailable. Please try again later.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }
  
  // Generic fallback
  return error.message || 'An unexpected error occurred. Please try again.';
}

/**
 * Extract field-specific validation errors
 */
export function getFieldErrors(error: ApiError): Record<string, string> {
  const responseData = error.response?.data;
  
  if (responseData && 'field_errors' in responseData && responseData.field_errors) {
    return responseData.field_errors;
  }
  
  return {};
}

/**
 * Check if error is due to authentication issues
 */
export function isAuthError(error: ApiError): boolean {
  const status = error.response?.status;
  const responseData = error.response?.data;
  const errorCode = responseData && 'error_code' in responseData ? responseData.error_code : undefined;
  
  return status === 401 || 
         (typeof errorCode === 'string' && errorCode.startsWith('AUTH_')) || 
         false;
}

/**
 * Check if error is due to validation issues
 */
export function isValidationError(error: ApiError): boolean {
  const status = error.response?.status;
  const responseData = error.response?.data;
  const errorCode = responseData && 'error_code' in responseData ? responseData.error_code : undefined;
  
  return status === 400 || 
         status === 422 ||
         (typeof errorCode === 'string' && errorCode.startsWith('VAL_')) ||
         false;
}

/**
 * Check if error is due to authorization/permission issues
 */
export function isPermissionError(error: ApiError): boolean {
  const status = error.response?.status;
  const responseData = error.response?.data;
  const errorCode = responseData && 'error_code' in responseData ? responseData.error_code : undefined;
  
  return status === 403 || 
         errorCode === 'AUTH_003' ||
         false;
}

/**
 * Get error ID for support/debugging purposes
 */
export function getErrorId(error: ApiError): string | null {
  const responseData = error.response?.data;
  return responseData && 'error_id' in responseData ? responseData.error_id || null : null;
}

/**
 * Log error details for debugging (only in development)
 */
export function logError(error: ApiError, context?: any): void {
  if (process.env.NODE_ENV === 'development') {
    console.group('🚨 API Error');
    console.error('Error:', error);
    if (context) {
      console.log('Context:', context);
    }
    console.log('Error ID:', getErrorId(error));
    console.log('User Message:', getErrorMessage(error));
    console.groupEnd();
  }
}

/**
 * Handle common error scenarios with appropriate actions
 */
export function handleApiError(error: ApiError, options?: {
  onAuthError?: () => void;
  onValidationError?: (fieldErrors: Record<string, string>) => void;
  onPermissionError?: () => void;
  context?: any;
}): void {
  const { onAuthError, onValidationError, onPermissionError, context } = options || {};
  
  // Log error for debugging
  logError(error, context);
  
  // Handle specific error types
  if (isAuthError(error) && onAuthError) {
    onAuthError();
  } else if (isValidationError(error) && onValidationError) {
    const fieldErrors = getFieldErrors(error);
    onValidationError(fieldErrors);
  } else if (isPermissionError(error) && onPermissionError) {
    onPermissionError();
  }
}